import { FC, useState, useEffect } from 'react'
import { RetroWindow, RetroButton } from '@/components/retro'
import { useGameStore } from '@/store'
import { useGame } from '@/providers'
import type { TradeIntent } from '@/utils/tradeIntentParser'

interface PendingTradeModalProps {
  isOpen: boolean
  tradeIntent: TradeIntent
  onTradeComplete: (success: boolean, details?: { token: string; amount: number; type: 'buy' | 'sell' | 'swap' }) => void
  onCancel: () => void
}

export const PendingTradeModal: FC<PendingTradeModalProps> = ({
  isOpen,
  tradeIntent,
  onTradeComplete,
  onCancel,
}) => {
  const { session } = useGameStore()
  const { buyToken, sellToken, getAvailableTokens } = useGame()
  const [selectedToken, setSelectedToken] = useState(tradeIntent.token || '')
  const [selectedFromToken, setSelectedFromToken] = useState(tradeIntent.fromToken || '')
  const [amount, setAmount] = useState('')
  const [isExecuting, setIsExecuting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedToken(tradeIntent.token || '')
      setSelectedFromToken(tradeIntent.fromToken || '')
      setAmount(tradeIntent.amount && tradeIntent.amount > 0 ? String(tradeIntent.amount) : '')
      setError(null)
    }
  }, [isOpen, tradeIntent])

  if (!isOpen || !session) return null

  const isSwap = tradeIntent.action === 'swap'
  const isBuy = tradeIntent.action === 'buy'
  const availableTokens = getAvailableTokens()
  const playerHoldings = session.player.portfolio.holdings.filter(h => h.symbol !== 'USDC' && h.amount > 0)
  const cashBalance = session.player.portfolio.cashBalance

  // Token options depend on action type
  let tokenOptions: string[]
  if (isSwap) {
    // For swap "to" token: all available tokens except the from token
    tokenOptions = availableTokens.map(t => t.symbol).filter(s => s !== 'USDC' && s !== selectedFromToken)
  } else if (isBuy) {
    tokenOptions = availableTokens.map(t => t.symbol).filter(s => s !== 'USDC')
  } else {
    tokenOptions = playerHoldings.map(h => h.symbol)
  }

  // For swap: from token options (tokens the player holds)
  const fromTokenOptions = playerHoldings.map(h => h.symbol)

  // Get the from token holding for swap
  const fromHolding = isSwap ? playerHoldings.find(h => h.symbol === selectedFromToken) : null

  const handleExecute = async () => {
    if (!selectedToken) {
      setError('Please select a token')
      return
    }

    if (isSwap && !selectedFromToken) {
      setError('Please select the token to sell')
      return
    }

    const numAmount = parseFloat(amount)
    if (isNaN(numAmount) || numAmount <= 0) {
      setError('Please enter a valid amount')
      return
    }

    setIsExecuting(true)
    setError(null)

    try {
      if (isSwap) {
        // Swap = sell fromToken for USDC, then buy toToken with proceeds
        const sellResult = await sellToken(selectedFromToken, numAmount)
        if (!sellResult.success) {
          setError(sellResult.message || 'Failed to sell ' + selectedFromToken)
          setIsExecuting(false)
          return
        }

        // The sell gave us USDC - now buy the target token with all of it
        // sellResult includes the USDC amount received in the trade
        const usdcReceived = sellResult.trade?.toAmount || 0
        if (usdcReceived <= 0) {
          setError('Sell succeeded but received no USDC')
          setIsExecuting(false)
          return
        }

        const buyResult = await buyToken(selectedToken, usdcReceived)
        if (!buyResult.success) {
          setError('Sold ' + selectedFromToken + ' but failed to buy ' + selectedToken + ': ' + (buyResult.message || ''))
          setIsExecuting(false)
          return
        }

        onTradeComplete(true, {
          token: selectedToken,
          amount: numAmount,
          type: 'swap',
        })
      } else if (isBuy) {
        const result = await buyToken(selectedToken, numAmount)
        if (result.success) {
          onTradeComplete(true, {
            token: selectedToken,
            amount: numAmount,
            type: 'buy',
          })
        } else {
          setError(result.message || 'Trade failed')
        }
      } else {
        const result = await sellToken(selectedToken, numAmount)
        if (result.success) {
          onTradeComplete(true, {
            token: selectedToken,
            amount: numAmount,
            type: 'sell',
          })
        } else {
          setError(result.message || 'Trade failed')
        }
      }
    } catch (err) {
      setError('Trade execution failed')
    } finally {
      setIsExecuting(false)
    }
  }

  const handleMaxAmount = () => {
    if (isSwap) {
      if (fromHolding) {
        setAmount(String(fromHolding.amount))
      }
    } else if (isBuy) {
      setAmount(String(Math.floor(cashBalance * 0.99))) // Leave 1% for fees
    } else {
      const holding = playerHoldings.find(h => h.symbol === selectedToken)
      if (holding) {
        setAmount(String(holding.amount))
      }
    }
  }

  const handleSkip = () => {
    // User decided not to trade - continue with AI without trade
    onTradeComplete(false)
  }

  const orderTitle = isSwap
    ? 'EXECUTE SWAP ORDER'
    : 'EXECUTE ' + tradeIntent.action.toUpperCase() + ' ORDER'

  const amountLabel = isSwap
    ? 'AMOUNT (' + (selectedFromToken || 'tokens') + ' to sell)'
    : isBuy ? 'AMOUNT (USDC)' : 'AMOUNT (tokens)'

  const buttonLabel = isSwap ? 'SWAP NOW' : tradeIntent.action.toUpperCase() + ' NOW'
  const buttonColor = isSwap
    ? 'bg-game-accent hover:bg-game-accent/80'
    : isBuy ? 'bg-game-profit hover:bg-game-profit/80' : 'bg-game-loss hover:bg-game-loss/80'

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <RetroWindow
        title={orderTitle}
        className="max-w-md w-full"
        onClose={onCancel}
      >
        <div className="space-y-4">
          {/* What user said */}
          <div className="p-2 bg-game-bg border border-game-border">
            <p className="font-retro text-xs text-game-muted mb-1">You said:</p>
            <p className="font-retro text-sm text-game-text italic">"{tradeIntent.rawMessage}"</p>
          </div>

          {/* Instruction */}
          <p className="font-retro text-sm text-game-muted">
            {isSwap
              ? 'Swap tokens below. Sells first token for USDC, then buys second token.'
              : 'Execute your trade below, then the story will continue based on what you actually did.'}
          </p>

          {/* Swap: From Token Selection */}
          {isSwap && (
            <div>
              <label className="font-pixel text-xs text-game-muted block mb-1">
                SELL TOKEN
              </label>
              <select
                value={selectedFromToken}
                onChange={(e) => setSelectedFromToken(e.target.value)}
                className="w-full px-3 py-2 bg-game-bg border border-game-border font-mono text-sm text-game-text focus:border-game-accent focus:outline-none"
              >
                <option value="">Select token to sell...</option>
                {fromTokenOptions.map(token => (
                  <option key={token} value={token}>{token}</option>
                ))}
              </select>
              {selectedFromToken && fromHolding && (
                <p className="font-mono text-xs text-game-muted mt-1">
                  Holding: {fromHolding.amount.toLocaleString(undefined, { maximumFractionDigits: 4 })} {selectedFromToken}
                </p>
              )}
            </div>
          )}

          {/* Token Selection (buy target / sell source) */}
          <div>
            <label className="font-pixel text-xs text-game-muted block mb-1">
              {isSwap ? 'BUY TOKEN' : isBuy ? 'TOKEN TO BUY' : 'TOKEN TO SELL'}
            </label>
            <select
              value={selectedToken}
              onChange={(e) => setSelectedToken(e.target.value)}
              className="w-full px-3 py-2 bg-game-bg border border-game-border font-mono text-sm text-game-text focus:border-game-accent focus:outline-none"
            >
              <option value="">Select token...</option>
              {tokenOptions.map(token => (
                <option key={token} value={token}>{token}</option>
              ))}
            </select>
          </div>

          {/* Amount Input */}
          <div>
            <label className="font-pixel text-xs text-game-muted block mb-1">
              {amountLabel}
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={isSwap
                  ? (fromHolding ? 'Max: ' + fromHolding.amount.toLocaleString(undefined, { maximumFractionDigits: 4 }) : 'Enter amount')
                  : isBuy ? 'Max: $' + cashBalance.toLocaleString() : 'Enter amount'}
                className="flex-1 px-3 py-2 bg-game-bg border border-game-border font-mono text-sm text-game-text focus:border-game-accent focus:outline-none"
              />
              <button
                onClick={handleMaxAmount}
                className="px-3 py-2 bg-game-panel border border-game-border font-pixel text-xs text-game-muted hover:text-game-accent hover:border-game-accent"
              >
                MAX
              </button>
            </div>
            {isBuy && !isSwap && (
              <p className="font-mono text-xs text-game-muted mt-1">
                Available: ${cashBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </p>
            )}
            {!isBuy && !isSwap && selectedToken && (
              <p className="font-mono text-xs text-game-muted mt-1">
                Holding: {playerHoldings.find(h => h.symbol === selectedToken)?.amount.toLocaleString(undefined, { maximumFractionDigits: 4 }) || 0} {selectedToken}
              </p>
            )}
          </div>

          {/* Error */}
          {error && (
            <p className="font-retro text-sm text-game-loss">{error}</p>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <RetroButton
              variant="default"
              className="flex-1"
              onClick={handleSkip}
              disabled={isExecuting}
            >
              SKIP TRADE
            </RetroButton>
            <RetroButton
              variant="primary"
              className={'flex-1 ' + buttonColor}
              onClick={handleExecute}
              disabled={isExecuting || !selectedToken || !amount || (isSwap && !selectedFromToken)}
            >
              {isExecuting ? 'EXECUTING...' : buttonLabel}
            </RetroButton>
          </div>

          {/* Skip explanation */}
          <p className="font-retro text-xs text-game-muted text-center">
            Skip if you changed your mind - the story will continue without the trade
          </p>
        </div>
      </RetroWindow>
    </div>
  )
}
